import type { ComponentType } from 'react'
import { SiteLayout } from '../components/SiteLayout'
import { formatDate, type ContentMeta } from '../lib/content'
import { SITE_URL } from '../lib/site'

/**
 * Research-paper template for `src/content/research/*.mdx`, deliberately not
 * the blog's ArticlePage: papers get an arXiv-style header (title, author
 * list, date, corpus line, abstract), narrower measure, numbered-section
 * typography via `.paper`, and a citation block. The MDX body supplies the
 * numbered sections and figures; everything above §1 is rendered from
 * frontmatter so every paper carries the same header shape.
 *
 * Math: `$…$` and `$$…$$` in the body are rendered to static KaTeX HTML at
 * build time (remark-math + rehype-katex in vite.config.ts), so nothing
 * client-side is needed to read the formulas.
 */
export function PaperPage({
  slug,
  meta,
  Content,
}: {
  slug: string
  meta: ContentMeta
  Content?: ComponentType
}) {
  const authors = meta.authors?.length ? meta.authors : meta.author ? [meta.author] : []
  const year = meta.date ? new Date(meta.date).getUTCFullYear() : new Date().getUTCFullYear()
  const url = `${SITE_URL}/research/${slug}`
  const citation =
    `${authors.join(', ')}. "${meta.title}." LangWatch Research, ${year}. ${url}`

  return (
    <SiteLayout>
      <article className="pt-28 pb-20 md:pt-32 md:pb-28">
        <header className="container-prose">
          <a
            href="/research"
            className="text-[13px] font-medium text-ink-500 transition-colors hover:text-ink-900"
          >
            ← Research
          </a>

          <p className="mt-6 text-[12px] font-medium uppercase tracking-[0.14em] text-ink-500">
            LangWatch Research
          </p>

          <h1 className="paper-title mt-3 text-[27px] leading-[1.18] text-ink-900 md:text-[36px]">
            {meta.title}
          </h1>

          {authors.length > 0 && (
            <p className="mt-5 text-[16px] text-ink-800">{authors.join(', ')}</p>
          )}

          <p className="mt-1.5 text-[14px] text-ink-500">
            {[meta.date ? formatDate(meta.date) : null, meta.dataset].filter(Boolean).join('  ·  ')}
          </p>

          {/* Set smaller and inset on both sides, the way the LaTeX article
              class sets an abstract, so it reads as a summary block rather
              than as the first section of the body. */}
          {meta.abstract && (
            <section className="mt-9 border-y border-ink-100 py-7 md:px-12">
              <h2 className="text-[12px] font-semibold uppercase tracking-[0.12em] text-ink-500">
                Abstract
              </h2>
              <p className="mt-3 text-[14.5px] leading-[1.68] text-ink-700">{meta.abstract}</p>
            </section>
          )}

          {meta.companionHref && meta.companionLabel && (
            <p className="mt-6 text-[15px] text-ink-600">
              A shorter, narrative version of this study is on the blog:{' '}
              <a href={meta.companionHref} className="underline underline-offset-2">
                {meta.companionLabel}
              </a>
              .
            </p>
          )}
        </header>

        <div className="container-prose prose paper mt-12 md:mt-14">
          {Content ? <Content /> : null}
        </div>

        <footer className="container-prose mt-14 md:mt-16">
          <div className="border-t border-ink-100 pt-8">
            <h2 className="text-[13px] font-semibold uppercase tracking-[0.12em] text-ink-500">
              Cite this
            </h2>
            <p className="mt-3 overflow-x-auto rounded-xl bg-ink-50 px-4 py-3 font-mono text-[13px] leading-[1.6] text-ink-700">
              {citation}
            </p>
          </div>
        </footer>
      </article>
    </SiteLayout>
  )
}
